<template>
  <div class="relative inline-block">
    <div @mousedown.prevent.stop="onSwitchClick" v-if="isSwitch">
      <BaseSwitch
        ref="trigger"
        :modelValue="modelValue"
        :readonly="true"
        :id="switchId"
        :label="deleteText"
      />
    </div>
    <div @mousedown.prevent.stop="onSwitchClick" v-else>
      <BaseButton
        ref="trigger"
        :modelValue="modelValue"
        :label="deleteText"
        type="danger"
        @click="handleDelete"
        :loading="loading"
        :disabled="disabled"
        :class="class"
        :id="switchId"
      />
    </div>
    <Popover
      ref="popover"
      :target="popoverTarget"
      :show-close-icon="false"
      :class="[
        'confirmation-popover shadow-lg rounded-lg overflow-hidden !m-0 bg-white',
        width,
        height,
      ]"
      @hide="onHide"
      data-test-id="ConfirmationPopover"
    >
      <div class="w-full sm:max-w-[400px] md:max-w-[480px] p-0"
      >
        <div
          v-if="header"
          class="flex items-center justify-between bg-greyLight text-white px-4 py-3 w-full"
        >
          <span
            class="font-just font-medium text-[14px] leading-[100%] tracking-[0%]"
          >
            {{ header }}
          </span>
          <button
            class="border-none bg-transparent justify-center border-0 text-white text-xl cursor-pointer p-0 flex items-center"
            @click="closePopover"
            :aria-label="$t('accessibilityLabels.closeMenu')"
            v-html="cancelSvg"
            autofocus="true"
          ></button>
        </div>

        <slot :close="closePopover">
          <template v-if="items && items.length">
            <ul class="list-none m-0 p-0">
              <li
                v-for="(item, idx) in items"
                :key="idx"
                :class="[
                  'flex items-center gap-3 px-3 py-4 cursor-pointer border-t-0 border-x-0 border-mutedStone text-base bg-white transition',
                  confirmingIdx !== null && confirmingIdx !== idx
                    ? 'text-gray-300 cursor-not-allowed'
                    : '',
                  item.danger ? 'text-[#a94442]' : '',
                  !item.disabled && confirmingIdx === null
                    ? 'hover:bg-gray-100'
                    : '',
                  idx === items.length - 1 ? '' : 'border-b border-solid',
                  item.class || '',
                  confirmingIdx === idx ? 'h-[71px]' : 'h-[62px]',
                ]"
                @click="() => handleItemClick(item, idx, closePopover)"
                :disabled="
                  item.disabled ||
                  (confirmingIdx !== null && confirmingIdx !== idx)
                "
              >
                <template v-if="confirmingIdx === idx">
                  <div class="flex flex-col w-full items-center cursor-default">
                    <span
                      class="font-just font-500 text-[16px] leading-[100%] tracking-[0%] text-alert mb-2"
                    >
                      {{ $t('commonLabels.areYouSure') }}
                    </span>
                    <div class="flex gap-2 w-full">
                      <button
                        style="background: rgba(138, 151, 152, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-softGray"
                        @click.stop="handleCancelConfirm"
                      >
                        {{ $t('commonLabels.noCancel') }}
                      </button>
                      <button
                        style="background: rgba(134, 51, 41, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-alert"
                        @click.stop="handleConfirm(idx, closePopover, item)"
                      >
                        {{ $t('commonLabels.yesDelete') }}
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span
                    v-if="item.icon"
                    class="w-5 h-5"
                    v-html="item.icon"
                  ></span>
                  <span
                    class="flex-1 text-btnActive font-just font-medium text-[16px] leading-[100%] tracking-[0%]"
                  >
                    {{ item.label }}
                  </span>
                </template>
              </li>
            </ul>
          </template>
        </slot>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import Popover from "primevue/popover";
import { closeSvg } from "@/utils/commonSvg";
import { beneficiaryLabels } from "@/utils/label";
import { usePopoverConfirmation } from "@/composables/usePopoverConfirmation";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  header: {
    type: String,
    default: "",
  },
  modelValue: Boolean,
  width: {
    type: String,
    default: "w-[272px]",
  },
  height: {
    type: String,
    default: "h-auto",
  },
  switchId: {
    type: String,
    default: "confirmation-switch",
  },
  isSwitch: {
    type: Boolean,
    default: true,
  },
  deleteText: { type: String, default: beneficiaryLabels.deleteBeneficiary },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  class: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);

const trigger = ref(null);

const {
  popover,
  popoverTarget,
  confirmingIdx,
  togglePopover,
  closePopover,
  onHide,
  handleItemClick,
  handleConfirm,
  handleCancelConfirm,
} = usePopoverConfirmation(trigger, emit);

const cancelSvg = computed(() =>
  closeSvg({ width: "24px", height: "24px", color: "white" })
);

function onSwitchClick(event) {
  // For button mode (isSwitch = false), always show popup
  if (!props.isSwitch) {
    togglePopover(event);
    return;
  }

  // For switch mode, only show popup if currently false
  if (props.modelValue) {
    return;
  }

  togglePopover(event);
}
</script>

<style>
/* Mobile styles */
@media (max-width: 768px) {
  .p-popover {
    position: fixed !important;
    width: 90% !important;
    max-width: 400px !important;
    margin: 0 !important;
    z-index: 2999 !important;
  }
}

.p-popover .p-popover-content {
  padding: 0;
}

.p-popover {
  border: 0px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}
</style>
